import { ACTIVITY_TYPE_LABELS } from '../../constants/activityTypes.js'
import { useEffect, useMemo, useState } from 'react'
import { getPipelineActivities } from '../../db/api.js'
import { cardActivityState } from '../../lib/cardActivity.js'
import Icon from '../../components/icons.jsx'

const types = ACTIVITY_TYPE_LABELS
const parseDate = (value) => new Date(/^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00` : value)
const dayKey = (date) => `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`
const today0 = () => {
  const date = new Date()
  date.setHours(0, 0, 0, 0)
  return date
}
function mondayOf(source) {
  const date = new Date(source)
  date.setHours(0, 0, 0, 0)
  date.setDate(date.getDate() - (date.getDay() + 6) % 7)
  return date
}
const MONTH_VISIBLE = 3
const activityState = (activity) => (activity.status === 'feito' ? 'done' : cardActivityState(activity.value))
const STATUS_FILTERS = [
  { key: 'todas', label: 'Todas', match: () => true },
  { key: 'agendadas', label: 'Agendadas', match: (state) => state === 'today' || state === 'future' },
  { key: 'atrasadas', label: 'Atrasadas', match: (state) => state === 'overdue' },
  { key: 'concluidas', label: 'Concluídas', match: (state) => state === 'done' },
]

export default function PipelineAgenda({ pipelineId, deals, search, dueOnly, onOpenDeal }) {
  const [mode, setMode] = useState('semana')
  const [cursor, setCursor] = useState(today0)
  const [expandedDay, setExpandedDay] = useState(null)
  const [statusFilter, setStatusFilter] = useState('todas')
  const [activities, setActivities] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [retry, setRetry] = useState(0)
  const dealsById = useMemo(() => new Map(deals.map((deal) => [deal.id, deal])), [deals])
  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError('')
    setActivities([])
    async function load() {
      try {
        // Uma única chamada para todas as atividades do funil (evita 1 request
        // por card). Descarta atividades de deals que não estão mais no board
        // carregado (ex.: arquivados entre uma leitura e outra).
        const rows = await getPipelineActivities(pipelineId)
        const results = rows.flatMap((activity) => {
          const deal = dealsById.get(activity.deal_id)
          return deal ? [{ ...activity, deal }] : []
        })
        if (!cancelled) setActivities(results)
      } catch {
        if (!cancelled) setError('Não foi possível carregar as atividades deste funil.')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [pipelineId, deals, retry])
  const isMonth = mode === 'mes'
  const days = useMemo(() => {
    if (!isMonth) {
      const start = mondayOf(cursor)
      return Array.from({ length: 7 }, (_, index) => new Date(start.getFullYear(), start.getMonth(), start.getDate() + index))
    }
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1)
    const start = mondayOf(first)
    const total = Math.ceil(((first.getDay() + 6) % 7 + new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate()) / 7) * 7
    return Array.from({ length: total }, (_, index) => new Date(start.getFullYear(), start.getMonth(), start.getDate() + index))
  }, [cursor, isMonth])
  const searched = useMemo(() => activities.flatMap((activity) => {
    const value = activity.inicio_at || activity.prazo
    if (!value) return []
    const date = parseDate(value)
    if (Number.isNaN(date.getTime())) return []
    const q = search.trim().toLowerCase()
    const digits = q.replace(/\D/g, '')
    const textHit = [activity.titulo, activity.deal.titulo, activity.deal.primary_lead, activity.deal.company_name, activity.deal.owner_nome, activity.deal.primary_lead_email, activity.deal.primary_lead_phone, activity.deal.company_cnpj].some((v) => (v || '').toLowerCase().includes(q))
    const digitHit = digits.length >= 3 && [activity.deal.primary_lead_phone, activity.deal.company_cnpj].some((v) => (v || '').replace(/\D/g, '').includes(digits))
    if (q && !textHit && !digitHit) return []
    if (dueOnly && (activity.status === 'feito' || !['today', 'overdue'].includes(cardActivityState(value)))) return []
    return [{ ...activity, date, value }]
  }).sort((a, b) => a.date - b.date), [activities, search, dueOnly])
  const statusCounts = useMemo(() => {
    const counts = Object.fromEntries(STATUS_FILTERS.map((f) => [f.key, 0]))
    for (const activity of searched) {
      const state = activityState(activity)
      for (const f of STATUS_FILTERS) if (f.match(state)) counts[f.key] += 1
    }
    return counts
  }, [searched])
  const scheduled = useMemo(() => {
    const filter = STATUS_FILTERS.find((f) => f.key === statusFilter) || STATUS_FILTERS[0]
    return searched.filter((activity) => filter.match(activityState(activity)))
  }, [searched, statusFilter])
  const entriesByDay = useMemo(() => {
    const map = new Map()
    for (const activity of scheduled) {
      const key = dayKey(activity.date)
      if (!map.has(key)) map.set(key, [])
      map.get(key).push(activity)
    }
    return map
  }, [scheduled])
  const move = (direction) => {
    setExpandedDay(null)
    setCursor((current) => isMonth
      ? new Date(current.getFullYear(), current.getMonth() + direction, 1)
      : new Date(current.getFullYear(), current.getMonth(), current.getDate() + direction * 7))
  }
  const changeMode = (next) => {
    if (next === mode) return
    setExpandedDay(null)
    // Ao voltar para a semana, prefere a semana atual se ela estiver dentro do mês exibido.
    if (next === 'semana' && cursor.getFullYear() === new Date().getFullYear() && cursor.getMonth() === new Date().getMonth()) setCursor(today0())
    setMode(next)
  }
  const openWeekOf = (day) => { setExpandedDay(null); setCursor(day); setMode('semana') }
  const dateLabel = (date) => date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })
  const monthLabel = cursor.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })
  const visibleCount = isMonth
    ? days.filter((day) => day.getMonth() === cursor.getMonth()).reduce((sum, day) => sum + (entriesByDay.get(dayKey(day))?.length || 0), 0)
    : days.reduce((sum, day) => sum + (entriesByDay.get(dayKey(day))?.length || 0), 0)
  const weekdays = days.slice(0, 7).map((day) => day.toLocaleDateString('pt-BR', { weekday: 'short' }))
  const monthGrid = <div className="pl-agenda-scroll"><div className="pl-agenda-month" role="grid" aria-label={monthLabel}>
    {weekdays.map((name) => <div key={name} role="columnheader" className="pl-agenda-month-weekday">{name}</div>)}
    {days.map((day) => {
      const key = dayKey(day)
      const entries = entriesByDay.get(key) || []
      const outside = day.getMonth() !== cursor.getMonth()
      const isToday = key === dayKey(new Date())
      const expanded = expandedDay === key
      const shown = expanded ? entries : entries.slice(0, MONTH_VISIBLE)
      return <div key={key} role="gridcell" className={`pl-agenda-month-cell${outside ? ' is-outside' : ''}${isToday ? ' is-today' : ''}`}>
        <button type="button" className="pl-agenda-month-day" title="Ver esta semana" aria-label={`Ver semana de ${day.toLocaleDateString('pt-BR', { day: 'numeric', month: 'long' })}`} onClick={() => openWeekOf(day)}>{day.getDate()}</button>
        <div className="pl-agenda-month-entries">
          {shown.map((activity) => {
            const state = activityState(activity)
            const time = activity.value.length === 10 ? '' : activity.date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
            return <button type="button" key={`${activity.deal.id}-${activity.id}`} className={`pl-agenda-chip pl-agenda-entry--${state}`} title={`${activity.titulo || 'Atividade sem título'} · ${activity.deal.titulo}`} onClick={() => onOpenDeal(activity.deal.id)}>
              {time && <span className="pl-agenda-chip-time">{time}</span>}<span className="pl-agenda-chip-title">{activity.titulo || 'Atividade sem título'}</span>
            </button>
          })}
          {entries.length > MONTH_VISIBLE && <button type="button" className="pl-agenda-more" aria-expanded={expanded} onClick={() => setExpandedDay(expanded ? null : key)}>{expanded ? 'Mostrar menos' : `+${entries.length - MONTH_VISIBLE} mais`}</button>}
        </div>
      </div>
    })}
  </div></div>
  return <section className="pl-agenda" aria-label={isMonth ? 'Agenda mensal do funil' : 'Agenda semanal do funil'}>
    <div className="pl-agenda-toolbar">
      <div><strong className={isMonth ? 'pl-agenda-month-label' : undefined}>{isMonth ? monthLabel : `${dateLabel(days[0])} — ${dateLabel(days[6])}`}</strong><span className="pl-agenda-total">{visibleCount} atividades {isMonth ? 'no mês' : 'na semana'}</span></div>
      <div className="pl-agenda-nav">
        <div className="pl-agenda-mode" role="group" aria-label="Filtrar atividades por situação">
          {STATUS_FILTERS.map((f) => <button type="button" key={f.key} aria-pressed={statusFilter === f.key} className={statusFilter === f.key ? 'is-active' : ''} onClick={() => { setExpandedDay(null); setStatusFilter(f.key) }}>{f.label} <span className="pl-agenda-filter-count">{statusCounts[f.key]}</span></button>)}
        </div>
        <div className="pl-agenda-mode" role="group" aria-label="Período da agenda">
          <button type="button" aria-pressed={!isMonth} className={!isMonth ? 'is-active' : ''} onClick={() => changeMode('semana')}>Semana</button>
          <button type="button" aria-pressed={isMonth} className={isMonth ? 'is-active' : ''} onClick={() => changeMode('mes')}>Mês</button>
        </div>
        <button type="button" className="pl-icon-btn" aria-label={isMonth ? 'Mês anterior' : 'Semana anterior'} onClick={() => move(-1)}>‹</button>
        <button type="button" className="pl-ghost-btn" onClick={() => { setExpandedDay(null); setCursor(today0()) }}>{isMonth ? 'Este mês' : 'Esta semana'}</button>
        <button type="button" className="pl-icon-btn" aria-label={isMonth ? 'Próximo mês' : 'Próxima semana'} onClick={() => move(1)}>›</button>
      </div>
    </div>
    {loading ? <p role="status">Carregando atividades…</p> : error ? <div role="alert">{error} <button className="pl-link-btn" onClick={() => setRetry((value) => value + 1)}>Tentar novamente</button></div> : <>
      {dueOnly && <p className="pl-agenda-hint">Exibindo atividades de hoje e atrasadas dentro d{isMonth ? 'o mês' : 'a semana'} selecionad{isMonth ? 'o' : 'a'}.</p>}
      {isMonth ? monthGrid : <>
      <div className="pl-agenda-scroll"><div className="pl-agenda-grid">
        {days.map((day) => {
          const entries = entriesByDay.get(dayKey(day)) || []
          const today = dayKey(day) === dayKey(new Date())
          const completedCount = entries.filter((activity) => activity.status === 'feito').length
          const overdueCount = entries.filter((activity) => activity.status !== 'feito' && cardActivityState(activity.value) === 'overdue').length
          const scheduledCount = entries.length - completedCount - overdueCount
          return <section key={dayKey(day)} className={`pl-agenda-day${today ? ' is-today' : ''}`}>
            <header>
              <div className="pl-agenda-day-date"><span>{day.toLocaleDateString('pt-BR', { weekday: 'short' })}</span><strong>{day.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}</strong>{today && <span className="pl-agenda-today-label">Hoje</span>}</div>
              <div className="pl-agenda-day-counts">
                <span title="Agendadas" aria-label={`${scheduledCount} atividades agendadas`}><Icon n="calendar" s={14} c="var(--sec)" />{scheduledCount}</span>
                <span title="Atrasadas" aria-label={`${overdueCount} atividades atrasadas`}><Icon n="other" s={14} c="var(--red)" />{overdueCount}</span>
                <span title="Concluídas" aria-label={`${completedCount} atividades concluídas`}><Icon n="check" s={14} c="#15803d" />{completedCount}</span>
              </div>
            </header>
            <div className="pl-agenda-entries">
              {!entries.length && <span className="pl-agenda-empty">Sem atividades</span>}
              {entries.map((activity) => {
                const state = activityState(activity)
                return <button type="button" key={`${activity.deal.id}-${activity.id}`} className={`pl-agenda-entry pl-agenda-entry--${state}`} onClick={() => onOpenDeal(activity.deal.id)}>
                  <span className="pl-agenda-entry-head">
                    <span className="pl-agenda-time">{activity.value.length === 10 ? 'Sem horário' : activity.date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })} · {types[activity.tipo] || activity.tipo || 'Atividade'}</span>
                    {(state === 'overdue' || state === 'done') && <span className="pl-agenda-status-icon" title={state === 'done' ? 'Concluída' : 'Atrasada'}>
                      <Icon n={state === 'done' ? 'check' : 'other'} s={15} c={state === 'done' ? '#15803d' : 'var(--red)'} />
                    </span>}
                  </span>
                  <strong>{activity.titulo || 'Atividade sem título'}</strong>
                  <span>{activity.deal.titulo}</span>
                  {activity.deal.primary_lead && <span className="pl-agenda-detail">{activity.deal.primary_lead}</span>}
                  <span className="pl-agenda-detail">{state === 'done' ? 'Concluída' : state === 'overdue' ? 'Atrasada' : state === 'today' ? 'Hoje' : 'Agendada'}</span>
                </button>
              })}
            </div>
          </section>
        })}
      </div></div>
      </>}
    </>}
  </section>
}
